'use client';

/**
 * GroupCard — one advocate-name variant as surfaced by the grouper.
 *
 * Auto-ticked groups (surname match + recent activity) are selected on
 * mount; un-ticked groups start unchecked. The group-level toggle selects
 * or clears every case in the group at once. Individual CaseRow tickboxes
 * let the user override per-row.
 */

import { useState, useMemo } from 'react';
import CaseRow from './CaseRow';
import { caseKey } from './caseKey';
import type { ResultGroup } from '../types';

interface GroupCardProps {
  group: ResultGroup;
  selectedKeys: Set<string>;
  onToggleCase: (caseKey: string, next: boolean) => void;
  onToggleGroup: (groupKey: string, next: boolean) => void;
}

const INITIAL_VISIBLE = 3;

export default function GroupCard({ group, selectedKeys, onToggleCase, onToggleGroup }: GroupCardProps) {
  const [expanded, setExpanded] = useState(false);
  const allCases = group.cases ?? [];
  const visibleCases = expanded ? allCases : allCases.slice(0, INITIAL_VISIBLE);

  const selectedInGroup = useMemo(() => {
    let n = 0;
    for (const c of allCases) if (selectedKeys.has(caseKey(c))) n++;
    return n;
  }, [allCases, selectedKeys]);

  const allSelected = selectedInGroup === allCases.length && allCases.length > 0;
  const someSelected = selectedInGroup > 0 && !allSelected;

  const hidden = Math.max(0, allCases.length - visibleCases.length);

  return (
    <article
      className={
        'rounded-xl border bg-white p-5 sm:p-6 transition ' +
        (allSelected ? 'border-primary-500 shadow-sm ring-1 ring-primary-200' : 'border-line')
      }
    >
      <header className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-ink leading-tight">
            {group.displayName}
          </h3>
          <p className="mt-1 text-sm text-ink-3">
            <span className="tabular-nums">{group.caseCount}</span> case{group.caseCount === 1 ? '' : 's'}
            {group.activeCount > 0 && (
              <> · <span className="tabular-nums">{group.activeCount}</span> active</>
            )}
            {group.reason && <span className="ml-2 text-xs text-ink-4">· {group.reason}</span>}
          </p>
        </div>
        <GroupToggle
          isSelected={allSelected}
          isPartial={someSelected}
          onToggle={next => onToggleGroup(group.groupingKey, next)}
        />
      </header>

      <ul className="mt-4 divide-y divide-line-soft">
        {visibleCases.map(c => {
          const key = caseKey(c);
          return (
            <CaseRow
              key={key}
              case_={c}
              selected={selectedKeys.has(key)}
              onToggle={next => onToggleCase(key, next)}
            />
          );
        })}
      </ul>

      {hidden > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-3 text-sm text-primary-700 underline-offset-4 hover:underline"
        >
          Show all {allCases.length} cases
        </button>
      )}
      {expanded && allCases.length > INITIAL_VISIBLE && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-3 text-sm text-ink-3 underline-offset-4 hover:underline"
        >
          Collapse
        </button>
      )}
    </article>
  );
}

function GroupToggle({
  isSelected,
  isPartial,
  onToggle,
}: {
  isSelected: boolean;
  isPartial: boolean;
  onToggle: (next: boolean) => void;
}) {
  const label = isSelected ? "That's me" : isPartial ? 'Some of these' : 'Not me';
  const base =
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-200';
  const selected = 'border-primary-600 bg-primary-50 text-primary-800 hover:bg-primary-100';
  const partial = 'border-accent-600 bg-accent-50 text-accent-800 hover:bg-accent-100';
  const unselected = 'border-line text-ink-3 hover:bg-paper-sunken';

  return (
    <button
      type="button"
      onClick={() => onToggle(!isSelected)}
      className={`${base} ${isSelected ? selected : isPartial ? partial : unselected}`}
      aria-pressed={isSelected}
    >
      <span aria-hidden="true">
        {isSelected ? '✓' : isPartial ? '◐' : '✗'}
      </span>
      {label}
    </button>
  );
}
